import React from "react";
import { Link } from "react-router-dom";
import { Github, Twitter, Linkedin, Database, Layers, ShieldCheck, HelpCircle } from "lucide-react";

export default function Footer() {
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.elements.newsletterEmail.value;
    // Standard mock behavior or connect to API if credentials set up
    console.log("Newsletter signup requested for email:", email);
    alert(`Thank you for subscribing, ${email}! We'll keep you updated.`);
    e.target.reset();
  };

  return (
    <footer className="bg-[#05050A] border-t border-white/10 text-[#ABA3C2] pt-20 pb-12 relative overflow-hidden" data-testid="footer-section">
      
      {/* Subtle background glow */}
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#7C3AED]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#EA7C3D]/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          
          {/* Logo & Info column */}
          <div className="lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center space-x-3 group" data-testid="footer-logo">
              <svg viewBox="0 0 100 100" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="50,10 90,90 10,90" fill="#4B2E83" />
                <polygon points="50,10 90,90 65,90" fill="#7C3AED" />
                <polygon points="68,68 85,90 55,90" fill="#EA7C3D" />
              </svg>
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-wider text-white font-display">AROVENIQ</span>
                <span className="text-[9px] text-[#ABA3C2] tracking-[0.25em] -mt-1 font-mono">
                  SOLUTIONS
                  <span className="inline-block w-1 h-1 bg-[#EA7C3D] rounded-full mx-1"></span>
                  Procurement
                </span>
              </div>
            </Link>
            <p className="text-sm max-w-sm text-[#ABA3C2]/80 font-body leading-relaxed">
              Aroveniq gives procurement and supply-chain teams a single, real-time intelligence layer on every vendor they work with — risk, performance, spend, and compliance, unified.
            </p>
            
            {/* Newsletter signup */}
            <div className="space-y-3 pt-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#6E6785]">Subscribe to our newsletter</div>
              <form onSubmit={handleNewsletterSubmit} className="flex max-w-sm relative" data-testid="newsletter-form">
                <input
                  type="email"
                  name="newsletterEmail"
                  placeholder="Enter your email"
                  required
                  data-testid="newsletter-input"
                  className="w-full bg-white/5 border border-white/10 rounded-full py-2.5 pl-5 pr-32 text-sm text-white focus:outline-none focus:border-[#7C3AED] transition-colors"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  className="absolute right-1 top-1 bottom-1 px-5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] hover:shadow-[0_0_15px_rgba(124,58,237,0.4)] text-white text-xs font-semibold tracking-wide transition-all"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          {/* Column 2: Platform */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white">Platform</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/platform/risk" className="hover:text-white transition-colors duration-200">Vendor Risk Intelligence</Link></li>
              <li><Link to="/platform/performance" className="hover:text-white transition-colors duration-200">Performance Analytics</Link></li>
              <li><Link to="/platform/spend" className="hover:text-white transition-colors duration-200">Spend & Cost Intelligence</Link></li>
              <li><Link to="/platform/compliance" className="hover:text-white transition-colors duration-200">Compliance & Audit</Link></li>
              <li><Link to="/platform/onboarding" className="hover:text-white transition-colors duration-200">Onboarding & KYC</Link></li>
              <li><Link to="/platform/contract" className="hover:text-white transition-colors duration-200">Contract Lifecycle</Link></li>
            </ul>
          </div>

          {/* Column 3: Industries */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white">Industries</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/industries/manufacturing" className="hover:text-white transition-colors duration-200">Manufacturing</Link></li>
              <li><Link to="/industries/retail" className="hover:text-white transition-colors duration-200">Retail & E-commerce</Link></li>
              <li><Link to="/industries/healthcare" className="hover:text-white transition-colors duration-200">Healthcare & Pharma</Link></li>
              <li><Link to="/industries/bfsi" className="hover:text-white transition-colors duration-200">BFSI</Link></li>
              <li><Link to="/industries/it" className="hover:text-white transition-colors duration-200">IT & Technology</Link></li>
              <li><Link to="/industries/logistics" className="hover:text-white transition-colors duration-200">Logistics & Supply Chain</Link></li>
            </ul>
          </div>

          {/* Column 4: Resources & About */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="hover:text-white transition-colors duration-200">About Us</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors duration-200">Pricing</Link></li>
              <li><Link to="/resources/blog" className="hover:text-white transition-colors duration-200">Blog & Resources</Link></li>
              <li><Link to="/resources/roi" className="hover:text-white transition-colors duration-200 font-semibold text-[#EA7C3D]">ROI Calculator</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-white transition-colors duration-200">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-white transition-colors duration-200">Terms of Service</Link></li>
            </ul>
          </div>

        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[#6E6785]">
            © {new Date().getFullYear()} Aroveniq Solutions LLP. All rights reserved.
          </div>
          
          {/* Social icons */}
          <div className="flex space-x-4">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 hover:bg-white/10 hover:text-white rounded-full transition-all" data-testid="social-linkedin">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 hover:bg-white/10 hover:text-white rounded-full transition-all" data-testid="social-twitter">
              <Twitter className="w-4 h-4" />
            </a>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 hover:bg-white/10 hover:text-white rounded-full transition-all" data-testid="social-github">
              <Github className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}